import React, { useEffect } from "react";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import ERPChatbot from "../../components/ERPChatbot/ERPChatbot";
import "./BigDataAnalytics.css";
import { Database, Cpu, BarChart3, Zap, TrendingUp, Layers } from "lucide-react";
import bigdata from "../../assets/bigdata.png";

export default function BigDataAnalytics() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Header />
      <div className="bigdata-page-content">
        {/* Hero Section */}
        <section
          className="bigdata-hero"
         style={{
    backgroundImage: `url(${bigdata})`,
  }}
        >
          <div className="bigdata-hero-overlay"></div>
          <h1 className="bigdata-hero-title">
            Drive Manufacturing Excellence with Integrated Big Data Analytics in Your ERP
          </h1>
        </section>

        {/* Intro Section */}
        <section className="bigdata-intro">
          <h2>Transform Data into Real-Time Strategic Insights</h2>
          <p>
            Big Data Analytics (BDA), when integrated within a Manufacturing ERP, helps organizations 
            transform vast, complex datasets into actionable intelligence for smarter planning, 
            production, and profitability. It combines operational, financial, and sensor-level data 
            to move from reactive to predictive decision-making.
          </p>
        </section>

        {/* Key Components */}
        <section className="bigdata-components">
          <h2>Key Components of Big Data Analytics in ERP</h2>
          <div className="bigdata-component-list">
            <div className="bigdata-component">
              <Database className="bigdata-icon" />
              <h3>Data Ingestion & Integration</h3>
              <p>
                Seamlessly collect, clean, and unify data from shop floors, external systems, 
                and spreadsheets into the ERP's central repository.
              </p>
            </div>

            <div className="bigdata-component">
              <Layers className="bigdata-icon" />
              <h3>Scalable Data Architecture</h3>
              <p>
                Handle the Volume, Velocity, and Variety of manufacturing Big Data using a 
                powerful data lake or warehouse for rapid analytics.
              </p>
            </div>

            <div className="bigdata-component">
              <Cpu className="bigdata-icon" />
              <h3>Advanced Analytical Models</h3>
              <p>
                Leverage AI and Machine Learning for forecasting, anomaly detection, 
                and optimization modeling across production and logistics.
              </p>
            </div>

            <div className="bigdata-component">
              <BarChart3 className="bigdata-icon" />
              <h3>Visualization & Reporting Engine</h3>
              <p>
                Turn complex analytics into clear dashboards and actionable visuals 
                tailored for every level of management.
              </p>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="bigdata-benefits">
          <h2>Core Benefits of ERP-Integrated Big Data Analytics</h2>
          <div className="bigdata-benefit-item">
            <Zap className="bigdata-benefit-icon" />
            <div>
              <h3>Optimized Production Planning</h3>
              <p>
                Use real-time data and demand forecasts to create accurate schedules, 
                minimize bottlenecks, and optimize machine utilization.
              </p>
            </div>
          </div>

          <div className="bigdata-benefit-item">
            <TrendingUp className="bigdata-benefit-icon" />
            <div>
              <h3>Predictive Cost & Profit Management</h3>
              <p>
                Forecast profitability by analyzing cost, efficiency, and sales data, 
                enabling dynamic pricing and strategy adjustments.
              </p>
            </div>
          </div>

          <div className="bigdata-benefit-item">
            <Database className="bigdata-benefit-icon" />
            <div>
              <h3>Enhanced Supply Chain Resiliency</h3>
              <p>
                Predict and mitigate supply chain disruptions through analytics on supplier 
                performance, logistics, and global trends.
              </p>
            </div>
          </div>

          <div className="bigdata-benefit-item">
            <Cpu className="bigdata-benefit-icon" />
            <div>
              <h3>Smarter Quality & Yield Improvement</h3>
              <p>
                Identify root causes of quality issues faster by correlating production 
                parameters with defect data.
              </p>
            </div>
          </div>

          <div className="bigdata-benefit-item">
            <BarChart3 className="bigdata-benefit-icon" />
            <div>
              <h3>Superior Demand Forecasting</h3>
              <p>
                Incorporate market and social factors alongside sales history 
                to achieve precise and dynamic demand forecasts.
              </p>
            </div>
          </div>
        </section>

        {/* Applications Section */}
        <section className="bigdata-applications">
          <h2>Key Big Data Applications on the Manufacturing Floor</h2>
          <div className="bigdata-app">
            <h3>Proactive Maintenance Scheduling</h3>
            <p>
              Integrate machine sensor data with ERP maintenance logs to predict failures 
              and auto-generate work orders before breakdowns occur.
            </p>
          </div>

          <div className="bigdata-app">
            <h3>Dynamic Inventory Optimization</h3>
            <p>
              Use analytics to determine optimal reorder points and safety stock levels, 
              balancing cost and availability in real time.
            </p>
          </div>

          <div className="bigdata-app">
            <h3>Manufacturing Process Simulation</h3>
            <p>
              Employ digital twin simulations to test process or equipment changes virtually, 
              improving efficiency before implementation.
            </p>
          </div>
        </section>

        {/* Closing Impact */}
        <section className="bigdata-impact">
          <h2>Data-Driven Manufacturing Starts Here</h2>
          <p>
            With Big Data Analytics integrated into your ERP, every department operates with 
            real-time intelligence—leading to faster decisions, lower costs, and stronger growth.
          </p>
        </section>
      </div>
      <ERPChatbot />
      <Footer />
    </>
  );
}
